import axios from 'axios';
import abena from './abenaSDK';
import { 
  mockPatients, 
  mockPatientDetails, 
  generateRealtimeVitals, 
  generateEcbomeTimeline,
  getPatientStats 
} from './mockPatientData';

const API_BASE    = 'https://api.abenaihr.com';
const PROVIDER_ID = 'PRV-31E3B5DC6535A93C';

// Helper: get the API key baked in at build time
const getApiKey = () => process.env.REACT_APP_ABENA_API_KEY || '';

// Base API configuration (for legacy axios calls elsewhere in the file)
const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use(
  (config) => {
    const key = getApiKey();
    if (key) config.headers['X-API-Key'] = key;
    config.headers['X-ABENA-Provider-Id'] = PROVIDER_ID;
    config.headers['X-ABENA-Timestamp'] = new Date().toISOString();
    return config;
  },
  (error) => Promise.reject(error)
);
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('abena_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Patient service methods
export const patientService = {
  // Get all patients — real API first, merged with mock roster as fallback
  getPatients: async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 200));

      let realPatients = [];
      try {
        const resp = await fetch(
          `${API_BASE}/provider/patients/${PROVIDER_ID}`,
          {
            headers: {
              'X-API-Key': getApiKey(),
              'X-ABENA-Provider-Id': PROVIDER_ID,
            },
          }
        );
        if (resp.ok) {
          const data = await resp.json();
          if (data.patients && data.patients.length > 0) {
            realPatients = data.patients.map(p => ({
              id: p.abena_patient_id,
              name: p.patient_name || 'Unknown',
              age: p.age || (p.date_of_birth
                ? Math.floor((Date.now() - new Date(p.date_of_birth)) / 31557600000)
                : '—'),
              gender: p.gender || '—',
              status: p.status || 'active',
              riskLevel: p.risk_level || 'medium',
              ecbomeScore: p.latest_ecbome_tone || 0.5,
              lastVisit: p.last_visit ? new Date(p.last_visit).toLocaleDateString() : '—',
              provider: p.provider_name || 'Dr. Williams',
              mrn: p.abena_patient_id,
              isRealPatient: true,
            }));
            console.log('✅ Real roster loaded:', realPatients.length, 'patients');
          }
        }
      } catch (apiErr) {
        console.warn('⚠️ Real API unavailable, using mock roster:', apiErr.message);
      }

      // Merge: real patients take precedence over same-ID mock patients
      const realIds = new Set(realPatients.map(p => p.id));
      const merged = [...realPatients, ...mockPatients.filter(p => !realIds.has(p.id))];
      console.log('✅ Patient roster:', merged.length, 'total');
      return merged;
    } catch (error) {
      console.error('❌ Failed to load patients:', error);
      return mockPatients; // never throw — return mock so UI doesn't crash
    }
  },

  // Get single patient row (for PatientList navigation)
  getPatient: async (patientId) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 150));
      const mock = mockPatients.find(p => p.id === patientId);
      return mock || { id: patientId, name: patientId };
    } catch (error) {
      console.error('❌ Failed to load patient:', error);
      throw new Error('Failed to load patient data');
    }
  },

  // Get full patient data — real API first, fallback to rich mock
  getPatientData: async (patientId) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 200));

      try {
        const resp = await fetch(
          `${API_BASE}/provider/patient/${patientId}/full`,
          {
            headers: {
              'X-API-Key': getApiKey(),
              'X-ABENA-Provider-Id': PROVIDER_ID,
            },
          }
        );
        if (resp.ok) {
          const d = await resp.json();
          const ecbome  = d.ecbome   || {};
          const quantum = d.quantum  || {};
          const chain   = d.chain    || {};
          const pat     = d.patient  || {};

          // Blend real scores into the mock data structure.
          // Keep mock recommendations/predictions (rich format the UI expects);
          // only update live numeric fields from the analyzer pipeline.
          const mockBase = mockPatientDetails[patientId] || {};
          const mockRow  = mockPatients.find(p => p.id === patientId) || {};

          const blended = {
            ...mockBase,
            patientInfo: {
              ...(mockBase.patientInfo || mockRow),
              id: patientId,
              name: pat.patient_name || mockRow.name || patientId,
              provider: pat.provider_name || mockRow.provider || '—',
            },
            ecbomeProfile: {
              ...(mockBase.ecbomeProfile || {}),
              score:  ecbome.ecbome_tone_index    != null ? ecbome.ecbome_tone_index    : (mockBase.ecbomeProfile?.score  ?? 0.5),
              cesd:   ecbome.cesd_proxy_score     != null ? ecbome.cesd_proxy_score     : mockBase.ecbomeProfile?.cesd,
              cb1:    ecbome.cb1_activity_proxy   != null ? ecbome.cb1_activity_proxy   : mockBase.ecbomeProfile?.cb1,
              cb2:    ecbome.cb2_demand_proxy      != null ? ecbome.cb2_demand_proxy     : mockBase.ecbomeProfile?.cb2,
              ppar:   ecbome.ppar_activation_proxy != null ? ecbome.ppar_activation_proxy : mockBase.ecbomeProfile?.ppar,
              hpa:    ecbome.hpa_ecbome_axis_score != null ? ecbome.hpa_ecbome_axis_score : mockBase.ecbomeProfile?.hpa,
              nae:    ecbome.nae_balance_index     != null ? ecbome.nae_balance_index    : mockBase.ecbomeProfile?.nae,
              panelCompleteness: ecbome.panel_completeness_pct,
              status: ecbome.ecbome_tone_index < 0.3
                ? 'severely_deficient'
                : ecbome.ecbome_tone_index < 0.6 ? 'deficient' : 'optimal',
            },
            quantumResult: {
              state:     quantum.optimal_treatment_state,
              prob:      quantum.optimal_treatment_prob,
              summary:   quantum.clinical_summary,
              topStates: quantum.top_5_states || [],
            },
            blockchain: {
              coinBalance:  chain.abena_coin_balance,
              attestations: chain.attestation_summary || {},
            },
            recommendations: mockBase.recommendations || [],
            alerts:          mockBase.alerts          || [],
            alertCount: {
              critical: (mockBase.alerts || []).filter(a => a.type === 'critical').length,
              warning:  (mockBase.alerts || []).filter(a => a.type === 'warning').length,
              info:     (mockBase.alerts || []).filter(a => a.type === 'info').length,
            },
            timestamp: new Date().toISOString(),
          };

          console.log('✅ Real eCBome/quantum/chain scores merged for:', patientId);
          return { success: true, data: blended };
        }
      } catch (apiErr) {
        console.warn('⚠️ Real API unavailable for', patientId, ':', apiErr.message);
      }

      // Fallback to mock
      const mockData = mockPatientDetails[patientId];
      if (mockData) {
        console.log('✅ Mock patient data loaded for:', patientId);
        return { success: true, data: mockData };
      }

      const mockRow = mockPatients.find(p => p.id === patientId);
      if (mockRow) {
        return {
          success: true,
          data: {
            patientInfo: mockRow,
            ecbomeProfile: { score: mockRow.ecbomeScore, status: 'Unknown', components: {} },
            recommendations: [],
            alerts: [],
            timestamp: new Date().toISOString(),
          },
        };
      }

      throw new Error(`Patient ${patientId} not found`);
    } catch (error) {
      console.error('❌ Failed to load patient data:', error);
      throw new Error('Failed to load patient data');
    }
  },

  // Get eCBome components - USE COMPREHENSIVE MOCK DATA
  getEcbomeComponents: async (patientId, timeRange = '24h') => {
    try {
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 250));
      
      const patientDetails = mockPatientDetails[patientId];
      if (!patientDetails) {
        throw new Error(`Patient ${patientId} not found`);
      }

      // Parse timeRange to get hours
      const hours = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : timeRange === '30d' ? 720 : 24;

      const ecbomeData = {
        success: true,
        data: {
          patientId,
          timeRange,
          components: patientDetails.ecbomeProfile.components,
          timeline: generateEcbomeTimeline(patientId, Math.min(hours, 24)), // Limit to 24 hours for performance
          overallScore: patientDetails.ecbomeProfile.score,
          status: patientDetails.ecbomeProfile.status,
          timestamp: new Date().toISOString()
        }
      };

      console.log('✅ eCBome components loaded for:', patientId);
      return ecbomeData;
    } catch (error) {
      console.error('❌ Failed to fetch eCBome components:', error);
      throw new Error('Failed to fetch eCBome components');
    }
  },

  // Get predictive alerts - USE COMPREHENSIVE MOCK DATA
  getPredictiveAlerts: async (patientId) => {
    try {
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const patientDetails = mockPatientDetails[patientId];
      if (!patientDetails) {
        throw new Error(`Patient ${patientId} not found`);
      }

      const alertsData = {
        success: true,
        data: {
          patientId,
          alerts: patientDetails.alerts || [],
          alertCount: {
            critical: patientDetails.alerts?.filter(a => a.type === 'critical').length || 0,
            warning: patientDetails.alerts?.filter(a => a.type === 'warning').length || 0,
            info: patientDetails.alerts?.filter(a => a.type === 'info').length || 0
          },
          timestamp: new Date().toISOString()
        }
      };

      console.log('✅ Predictive alerts loaded for:', patientId);
      return alertsData;
    } catch (error) {
      console.error('❌ Failed to fetch predictive alerts:', error);
      throw new Error('Failed to fetch predictive alerts');
    }
  },

  // Get clinical recommendations - USE COMPREHENSIVE MOCK DATA
  getClinicalRecommendations: async (patientId) => {
    try {
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 250));
      
      const patientDetails = mockPatientDetails[patientId];
      if (!patientDetails) {
        throw new Error(`Patient ${patientId} not found`);
      }

      const recommendationsData = {
        success: true,
        data: {
          patientId,
          recommendations: patientDetails.recommendations || [],
          recommendationCount: {
            high: patientDetails.recommendations?.filter(r => r.priority === 'high').length || 0,
            medium: patientDetails.recommendations?.filter(r => r.priority === 'medium').length || 0,
            low: patientDetails.recommendations?.filter(r => r.priority === 'low').length || 0,
            critical: patientDetails.recommendations?.filter(r => r.priority === 'critical').length || 0
          },
          timestamp: new Date().toISOString()
        }
      };

      console.log('✅ Clinical recommendations loaded for:', patientId);
      return recommendationsData;
    } catch (error) {
      console.error('❌ Failed to fetch clinical recommendations:', error);
      throw new Error('Failed to fetch clinical recommendations');
    }
  },

  // Send intervention - USE REAL API
  sendIntervention: async (patientId, intervention) => {
    try {
      const response = await api.post(`/patients/${patientId}/interventions`, intervention);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to send intervention:', error);
      throw new Error('Failed to send intervention to database');
    }
  },

  // Subscribe to real-time patient updates
  subscribeToPatient: (patientId, callback) => {
    try {
      return abena.subscribeToPatient(patientId, callback);
    } catch (error) {
      console.error('❌ Failed to subscribe to patient:', error);
      throw new Error('Failed to subscribe to patient updates');
    }
  },

  // Unsubscribe from real-time updates
  unsubscribeFromPatient: (subscriptionId) => {
    try {
      return abena.unsubscribe(subscriptionId);
    } catch (error) {
      console.error('❌ Failed to unsubscribe from patient:', error);
    }
  },

  // Create new patient - USE REAL API
  createPatient: async (patientData) => {
    try {
      const response = await api.post('/patients', patientData);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create patient:', error);
      throw new Error('Failed to create patient in database');
    }
  },

  // Update patient - USE REAL API
  updatePatient: async (patientId, patientData) => {
    try {
      const response = await api.put(`/patients/${patientId}`, patientData);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to update patient:', error);
      throw new Error('Failed to update patient in database');
    }
  },

  // Delete patient - USE REAL API
  deletePatient: async (patientId) => {
    try {
      await api.delete(`/patients/${patientId}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Failed to delete patient:', error);
      throw new Error('Failed to delete patient from database');
    }
  },

  // Search patients - USE REAL API
  searchPatients: async (query) => {
    try {
      const response = await api.get(`/patients/search?q=${query}`);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to search patients:', error);
      throw new Error('Failed to search patients in database');
    }
  },

  // Get patient history - USE COMPREHENSIVE MOCK DATA
  getPatientHistory: async (patientId, timeRange = '30d') => {
    try {
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      const patientDetails = mockPatientDetails[patientId];
      if (!patientDetails) {
        throw new Error(`Patient ${patientId} not found`);
      }

      // Generate historical data points
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 30;
      const historyPoints = [];

      for (let i = days; i >= 0; i--) {
        historyPoints.push({
          date: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString(),
          ecbomeScore: Math.max(0.3, Math.min(1, patientDetails.ecbomeProfile.score + (Math.random() * 0.1 - 0.05))),
          vitalSigns: generateRealtimeVitals(patientId),
          status: patientDetails.patientInfo.status
        });
      }

      const historyData = {
        success: true,
        data: {
          patientId,
          timeRange,
          history: historyPoints,
          trends: {
            ecbomeScore: patientDetails.ecbomeProfile.components.endocannabinoid.trend,
            overallHealth: 'stable'
          },
          timestamp: new Date().toISOString()
        }
      };

      console.log('✅ Patient history loaded for:', patientId);
      return historyData;
    } catch (error) {
      console.error('❌ Failed to fetch patient history:', error);
      throw new Error('Failed to fetch patient history');
    }
  },

  // Get system statistics - USE COMPREHENSIVE MOCK DATA
  getSystemStats: async () => {
    try {
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 150));
      
      const stats = getPatientStats();
      
      console.log('✅ System statistics loaded');
      return {
        success: true,
        data: stats
      };
    } catch (error) {
      console.error('❌ Failed to fetch system stats:', error);
      throw new Error('Failed to fetch system statistics');
    }
  },

  // Initialize ABENA SDK
  initializeABENA: async () => {
    try {
      await abena.initialize();
      console.log('✅ ABENA SDK initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize ABENA SDK:', error);
      throw new Error('Failed to initialize ABENA SDK');
    }
  }
}; 